import React, { useState, useEffect } from 'react';
import { X, RefreshCw, CheckCircle, AlertTriangle, ArrowRight, Upload, Download } from 'lucide-react';

const GoogleSheetsSyncModal = ({ isOpen, onClose, API_BASE_URL, onSyncComplete }) => {
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedChanges, setSelectedChanges] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchSyncPreview();
    } else {
      // Reset state when modal closes
      setSyncData(null);
      setError(null);
      setSelectedChanges(new Set());
    }
  }, [isOpen]);

  const fetchSyncPreview = async () => {
    console.log('========================================');
    console.log('🔄 FETCH SYNC PREVIEW - START');
    console.log('⏰ Timestamp:', new Date().toISOString());
    
    setComparing(true);
    setError(null);
    
    try {
      // Step 1: Check for existing pending changes in synchistory
      console.log('📡 Step 1: Checking for existing pending changes...');
      console.log('   URL:', `${API_BASE_URL}/api/googleSheetSync.php?action=get_history`);
      
      const historyResponse = await fetch(`${API_BASE_URL}/api/googleSheetSync.php?action=get_history`);
      
      if (!historyResponse.ok) {
        throw new Error(`HTTP ${historyResponse.status}: ${historyResponse.statusText}`);
      }
      
      const historyResult = await historyResponse.json();
      
      console.log('📥 Step 1 Response:', historyResult);
      console.log('   Success:', historyResult.success);
      console.log('   Changes count:', historyResult.data?.changes?.length || 0);
      
      // If there are already pending changes, just show them (don't re-run preview)
      if (historyResult.success && historyResult.data?.changes?.length > 0) {
        console.log('✅ Found existing pending changes, showing them without re-scanning');
        console.log('   Summary:', historyResult.data.summary);
        console.log('   Changes:', historyResult.data.changes);
        
        setSyncData(historyResult.data);
        const allChanges = new Set();
        historyResult.data.changes.forEach((_, index) => allChanges.add(index));
        setSelectedChanges(allChanges);
        setComparing(false);
        console.log('========================================');
        return;
      }
      
      // Step 2: No pending changes, so run preview to detect new ones
      console.log('🔍 Step 2: No pending changes found, running fresh scan...');
      console.log('📡 Calling preview endpoint...');
      console.log('   URL:', `${API_BASE_URL}/api/googleSheetSync.php?action=preview`);
      
      const previewResponse = await fetch(`${API_BASE_URL}/api/googleSheetSync.php?action=preview`);
      
      if (!previewResponse.ok) {
        throw new Error(`HTTP ${previewResponse.status}: ${previewResponse.statusText}`);
      }
      
      const previewResult = await previewResponse.json();
      
      console.log('📥 Step 2 Response:', previewResult);
      console.log('   Success:', previewResult.success);
      console.log('   Message:', previewResult.message);
      console.log('   Data:', previewResult.data);
      
      if (!previewResult.success) {
        console.error('❌ Preview failed:', previewResult.message);
        setError(previewResult.message || 'Failed to detect changes');
        setComparing(false);
        console.log('========================================');
        return;
      }
      
      // Step 3: Fetch the newly populated synchistory
      console.log('📡 Step 3: Fetching newly populated synchistory...');
      console.log('   URL:', `${API_BASE_URL}/api/googleSheetSync.php?action=get_history`);
      
      const response = await fetch(`${API_BASE_URL}/api/googleSheetSync.php?action=get_history`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      console.log('📥 Step 3 Response:', result);
      console.log('   Success:', result.success);
      console.log('   Changes count:', result.data?.changes?.length || 0);
      console.log('   Summary:', result.data?.summary);
      
      if (result.success) {
        console.log('✅ Setting sync data with', result.data?.changes?.length || 0, 'changes');
        console.log('   Changes:', result.data?.changes);
        
        setSyncData(result.data);
        
        // Auto-select all changes by default
        if (result.data?.changes?.length > 0) {
          const allChanges = new Set();
          result.data.changes.forEach((_, index) => allChanges.add(index));
          setSelectedChanges(allChanges);
        }
        
        console.log('✅ Sync data set successfully');
      } else {
        console.error('❌ Failed to fetch sync preview:', result.message);
        setError(result.message || 'Failed to fetch sync preview');
      }
    } catch (err) {
      console.error('❌ EXCEPTION in fetchSyncPreview:', err);
      console.error('   Error message:', err.message);
      console.error('   Stack:', err.stack);
      setError('Failed to connect to sync service: ' + err.message);
    } finally {
      setComparing(false);
      console.log('🏁 FETCH SYNC PREVIEW - END');
      console.log('========================================');
    }
  };

  const handleApplyChanges = async () => {
    if (selectedChanges.size === 0) {
      setError('Please select at least one change to apply');
      return;
    }

    console.log('========================================');
    console.log('🔄 APPLY CHANGES - START');
    console.log('Selected changes count:', selectedChanges.size);

    setLoading(true);
    setError(null);

    try {
      const changesToApply = syncData.changes.filter((_, index) => 
        selectedChanges.has(index)
      );

      console.log('📤 Changes to apply:', changesToApply);
console.log('📋 First change object:', JSON.stringify(changesToApply[0], null, 2));
console.log('🔑 Sync IDs being sent:', changesToApply.map(c => c.sync_id));
console.log('📡 Sending request to:', `${API_BASE_URL}/api/googleSheetSync.php?action=apply`);

      const response = await fetch(`${API_BASE_URL}/api/googleSheetSync.php?action=apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes: changesToApply })
      });

      console.log('📥 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📦 Response data:', result);

      if (result.success) {
        console.log('✅ Apply successful! Message:', result.message);
        console.log('🔄 Calling onSyncComplete...');
        onSyncComplete?.(result.message);
        console.log('🚪 Closing modal...');
        console.log('========================================');
        onClose();
      } else {
        console.error('❌ Apply failed:', result.message);
        console.log('========================================');
        setError(result.message || 'Failed to apply changes');
      }
    } catch (err) {
      console.error('❌ Exception during apply:', err);
      console.log('========================================');
      setError('Failed to apply changes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePushToSheet = async () => {
    console.log('========================================');
    console.log('📤 PUSH DB TO SHEET - START');
    
    setLoading(true);
    setError(null);

    try {
      console.log('📡 Sending request to:', `${API_BASE_URL}/api/googleSheetSync.php?action=push`);
      
      const response = await fetch(`${API_BASE_URL}/api/googleSheetSync.php?action=push`, {
        method: 'POST'
      });

      console.log('📥 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📦 Response data:', result);

      if (result.success) {
        console.log('✅ Push successful! Message:', result.message);
        onSyncComplete?.(result.message);
        console.log('========================================');
        onClose();
      } else {
        console.error('❌ Push failed:', result.message);
        console.log('========================================');
        setError(result.message || 'Failed to push to Google Sheet');
      }
    } catch (err) {
      console.error('❌ Exception during push:', err);
      console.log('========================================');
      setError('Failed to push to Google Sheet: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleChange = (index) => {
    const newSelected = new Set(selectedChanges);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedChanges(newSelected);
  };

  const toggleAll = () => {
    if (selectedChanges.size === syncData?.changes?.length) {
      setSelectedChanges(new Set());
    } else {
      const allChanges = new Set();
      syncData?.changes?.forEach((_, index) => allChanges.add(index));
      setSelectedChanges(allChanges);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center">
                <RefreshCw className="w-7 h-7 mr-3 text-emerald-600" />
                Google Sheets Sync
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Review and apply changes from Google Sheets to your database
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/80 rounded-xl transition-colors"
              disabled={loading || comparing}
            >
              <X className="w-6 h-6 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {comparing ? (
            <div className="flex flex-col items-center justify-center py-16">
              <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin mb-4" />
              <p className="text-lg font-medium text-slate-700">Comparing with Google Sheets...</p>
              <p className="text-sm text-slate-500 mt-2">This may take a few moments</p>
            </div>
          ) : syncData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">New Records</p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">
                        {syncData.summary?.new_count || 0}
                      </p>
                    </div>
                    <Upload className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-900">Updates</p>
                      <p className="text-2xl font-bold text-amber-600 mt-1">
                        {syncData.summary?.update_count || 0}
                      </p>
                    </div>
                    <RefreshCw className="w-8 h-8 text-amber-600" />
                  </div>
                </div>

                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-900">Deletions</p>
                      <p className="text-2xl font-bold text-red-600 mt-1">
                        {syncData.summary?.delete_count || 0}
                      </p>
                    </div>
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-emerald-900">Total Changes</p>
                      <p className="text-2xl font-bold text-emerald-600 mt-1">
                        {syncData.changes?.length || 0}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>
              </div>

              {!syncData.changes || syncData.changes.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Everything is in sync!
                  </h3>
                  <p className="text-slate-600">
                    Your database and Google Sheet are already synchronized.
                  </p>
                </div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="mb-4 flex items-center justify-between bg-slate-50 rounded-xl p-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedChanges.size === syncData.changes.length}
                        onChange={toggleAll}
                        className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                      />
                      <span className="ml-3 font-medium text-slate-900">
                        Select All ({syncData.changes.length} changes)
                      </span>
                    </label>
                    <span className="text-sm text-slate-600">
                      {selectedChanges.size} selected
                    </span>
                  </div>

                  {/* Changes List */}
                  <div className="space-y-3">
                    {syncData.changes.map((change, index) => (
                      <div
                        key={change.sync_id || index}
                        className={`border rounded-xl p-4 transition-all ${
                          selectedChanges.has(index)
                            ? 'border-emerald-300 bg-emerald-50'
                            : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            checked={selectedChanges.has(index)}
                            onChange={() => toggleChange(index)}
                            className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 mt-1"
                          />
                          
                          <div className="ml-4 flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                change.type === 'new' 
                                  ? 'bg-blue-100 text-blue-800'
                                  : change.type === 'delete'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}>
                                {change.type === 'new' ? 'New Record' : change.type === 'delete' ? 'Delete' : 'Update'}
                              </span>
                              <span className="font-semibold text-slate-900">
                                {change.candidate_name || '(No Name)'}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {change.type === 'update' && change.changes_detail ? (
                                Object.entries(change.changes_detail).map(([field, values]) => (
                                  <div key={field} className="bg-white rounded-lg p-3 border border-slate-200">
                                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">
                                      {field.replace(/_/g, ' ')}
                                    </p>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-slate-600 line-through text-xs break-all">
                                        {values.old || '(empty)'}
                                      </span>
                                      <ArrowRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                      <span className="text-emerald-600 font-medium text-xs break-all">
                                        {values.new || '(empty)'}
                                      </span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <>
                                  <div>
                                    <span className="text-slate-600">Email:</span>
                                    <span className="ml-2 font-medium break-all">{change.candidate_email || '(No Email)'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-600">Client:</span>
                                    <span className="ml-2 font-medium">{change.client || '(No Client)'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-600">Status:</span>
                                    <span className="ml-2 font-medium">{change.status || '(No Status)'}</span>
                                  </div>
                                  {change.detected_at && (
                                    <div className="col-span-full text-xs text-slate-500 mt-2">
                                      Detected: {new Date(change.detected_at).toLocaleString()}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          {/* <button
            onClick={handlePushToSheet}
            disabled={loading || comparing}
            className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            Push DB to Sheet
          </button> */}

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading || comparing}
              className="px-6 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyChanges}
              disabled={loading || comparing || selectedChanges.size === 0 || !syncData?.changes?.length}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Apply {selectedChanges.size > 0 ? `${selectedChanges.size} ` : ''}Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsSyncModal;